import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameView, LogEntry } from '../api.js';
import type { ReplayState } from './useReplay.js';

/**
 * Turn mode's playback: each turn replayed from the log once it is over.
 *
 * In turn mode the server runs all five registers, end of turn and the
 * next Deal in one go, so the view a player receives is already the next
 * turn. The live playback assumes the view IS the register just resolved,
 * which here it never is; the in-game "replay turn" plays a past turn
 * purely from the log while the view is elsewhere, which is exactly this.
 * So this starts that replay by itself, for every turn the player has not
 * seen, in order, and says when the table should be held back until it
 * has finished: programming and game over.
 *
 * A turn counts as replayed when its replay finishes or is stopped, not
 * when it starts, so a reload part-way through plays it again. The last
 * turn replayed is kept per game in this browser. A page load replays
 * only the most recent turn; while the page is open, every one.
 */

const STORE_PREFIX = 'rallyx.replayed.';

// Between steps. Quicker than a replay asked for from the log, because
// every turn is played this way and the table waits on it.
const TURN_STEP_MS = 350;

function storedTurn(gameId: number): number {
  try {
    const raw = window.localStorage.getItem(STORE_PREFIX + gameId);
    const n = raw === null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function storeTurn(gameId: number, turn: number): void {
  try {
    window.localStorage.setItem(STORE_PREFIX + gameId, String(turn));
  } catch {
    // Not remembered; at worst a reload replays the turn once more.
  }
}

/** The register entries of one turn, which is what a replay plays. */
function turnEntries(entries: LogEntry[], turn: number): LogEntry[] {
  return entries.filter((e) => e.turn === turn && e.register !== null && e.frame);
}

/** Whether the log holds this turn's registers to the end: the advance
 * that closes register 5 is the last of them. */
function registersLogged(entries: LogEntry[], turn: number): boolean {
  return entries.some((e) => (
    e.turn === turn && e.register === 5 && e.phase === 'awaitingRegisterChoices'
  ));
}

/**
 * The latest turn whose registers have been played, and whether the log
 * has all of them yet. Null while that cannot be told.
 *
 * A game over is read off the log: the game can end at any register, and
 * an end-of-turn finish has already moved the turn number on. The action
 * that ended it is the newest in the log once the log has caught up, and
 * until it has, the newest is an earlier turn's programming.
 */
function playedTurn(view: GameView, entries: LogEntry[], loaded: boolean):
  { turn: number; ready: boolean } | null {
  const phase = view.phase.kind;
  if (phase === 'gameOver') {
    const last = entries[0];
    if (!loaded || !last || last.turn === null) return null;
    if (last.phase !== 'awaitingRegisterChoices' && last.phase !== 'awaitingEndOfTurn') return null;
    return { turn: last.turn, ready: true };
  }
  // Turn mode runs through the end of a turn without stopping, so the
  // view is already the next turn's: the one to replay is the turn before.
  const turn = view.turnNumber - 1;
  return { turn, ready: loaded && registersLogged(entries, turn) };
}

export function useTurnReplay(
  gameId: number,
  view: GameView | null,
  entries: LogEntry[],
  loaded: boolean,
  replay: ReplayState,
): {
  /** Programming, end-of-turn questions and game over wait while true. */
  holding: boolean;
  /** The turn being replayed, or waited for. */
  turn: number | null;
  /** Ends the replay and releases everything it holds, queued turns too. */
  stopAll: () => void;
} {
  const enabled = view?.turnMode === true;
  const played = view && enabled ? playedTurn(view, entries, loaded) : null;
  // The server runs the turn as a run of advances, each committed, so a
  // view loaded while it is under way can land in the middle of one. There
  // is nothing to show then but the turn still running.
  const midTurn = view?.phase.kind === 'runningRegister'
    || view?.phase.kind === 'awaitingRegisterChoices';

  // The last turn this browser has replayed. Null until it is known which
  // turn has been played, which for a finished game waits on the log.
  const [handled, setHandled] = useState<number | null>(null);
  // The turn whose replay is running.
  const current = useRef<number | null>(null);
  const started = useRef(false);
  // Stopped before it could tell which turn to play: a finished game whose
  // log has not loaded. Nothing would otherwise release it.
  const [released, setReleased] = useState(false);

  useEffect(() => {
    setHandled(null);
    setReleased(false);
    current.current = null;
    started.current = false;
  }, [gameId]);

  // A page load replays the most recent turn at most: anything before it
  // counts as seen. Not decided mid-turn, when the turn about to finish
  // is not yet the one played.
  useEffect(() => {
    if (handled !== null || !played || midTurn) return;
    setHandled(Math.max(storedTurn(gameId), played.turn - 1));
  }, [handled, played, midTurn, gameId]);

  const finish = useCallback((turn: number) => {
    current.current = null;
    started.current = false;
    setHandled((h) => Math.max(h ?? 0, turn));
    storeTurn(gameId, Math.max(storedTurn(gameId), turn));
  }, [gameId]);

  // The next turn, once the log holds it and nothing is playing.
  useEffect(() => {
    if (!played || handled === null || current.current !== null) return;
    if (replay.replaying) return;
    const next = handled + 1;
    if (next > played.turn) return;
    // An earlier turn is in the log whole; the latest needs checking.
    if (next === played.turn && !played.ready) return;
    const queue = turnEntries(entries, next);
    if (queue.length === 0) {
      // Nothing recorded to play, so nothing to wait for.
      finish(next);
      return;
    }
    current.current = next;
    started.current = false;
    replay.startTurn(queue, TURN_STEP_MS);
  }, [played, handled, entries, replay, finish]);

  // Finished, or stopped from the log panel.
  useEffect(() => {
    if (current.current === null) return;
    if (replay.replaying) {
      started.current = true;
      return;
    }
    if (started.current) finish(current.current);
  }, [replay.replaying, finish]);

  const stopAll = useCallback(() => {
    const last = played?.turn ?? current.current ?? 0;
    replay.stop();
    if (!played) setReleased(true);
    finish(Math.max(last, current.current ?? 0));
  }, [played, replay, finish]);

  const holding = enabled && view !== null && view.phase.kind !== 'deal'
    && (midTurn || (
      !(released && !played)
      && (played === null || handled === null || handled < played.turn)
    ));

  return {
    holding,
    turn: holding ? (current.current ?? (handled ?? 0) + 1) : null,
    stopAll,
  };
}
